import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import { ArrowRight, Calendar, Layers, Sparkles, CheckCircle2 } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import ForwardPathsDiagram from '@/components/diagrams/ForwardPathsDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const COMPARISON_ROWS = [
  {
    dimension: 'What changes',
    pathA: 'Infrastructure (on-prem → OCI Marketplace VM)',
    pathB: 'AP automation platform (WebCenter → EZ Cloud)',
  },
  {
    dimension: 'What stays',
    pathA: 'WebCenter architecture, customizations, Forms Recognition templates, SOA composites, ADF forms',
    pathB: 'Oracle ERP (EBS or Fusion) as system of record, Oracle-supported integration patterns, audit trail',
  },
  {
    dimension: 'Typical timeline',
    pathA: '3-6 months for lift-and-shift; 6-12 months if combined with 12c → 14c upgrade',
    pathB: '4-9 months for full modernization with EZ Cloud platform',
  },
  {
    dimension: 'Maintenance burden after',
    pathA: 'Same WebCenter maintenance (templates, BPEL, ADF) on OCI infrastructure',
    pathB: 'No WebCenter maintenance; AI extraction learns from corrections; configuration-driven workflow',
  },
  {
    dimension: 'Next decision point',
    pathA: 'WebCenter 14c lifecycle (estimated 2032+); same modernization decision recurs',
    pathB: 'Strategic platform decision made once',
  },
  {
    dimension: 'AP capability advancement',
    pathA: 'Same as today (WebCenter feature set)',
    pathB: 'AI extraction, supplier-email exception prevention, modern non-PO coding UX',
  },
  {
    dimension: 'Oracle support posture',
    pathA: 'Oracle-supported on OCI Marketplace through 14c lifecycle',
    pathB: 'Oracle-supported integration patterns preserved (EBS Open Interface or OIC + FBDI)',
  },
  {
    dimension: 'Best fit',
    pathA: 'Customers deferring strategic decision; limited modernization budget in 6 months; AP requirements unchanged',
    pathB: 'Customers ready for the strategic decision; AP requirements have outpaced WebCenter; using the Dec 2026 forcing function',
  },
];

const PATH_A_WHEN = [
  'Your AP team is satisfied with current WebCenter functionality',
  'Your supplier mix is stable (low new-supplier rate)',
  'Your WebCenter customizations are extensive and well-maintained',
  'You don\'t have modernization budget allocated in the next 6-12 months',
  'You\'re using the 12c → 14c upgrade to defer the strategic decision',
  'You have skilled WebCenter operators in-house',
];

const PATH_B_WHEN = [
  'Your AP team is constrained by WFR template maintenance or the verifier queue',
  'Supplier-email triage is consuming AP team capacity (status chasing, missing PO numbers, etc.)',
  'You\'re losing WebCenter expertise (departure risk, hiring difficulty)',
  'Your ADF non-PO coding form requires development cycles for routine changes',
  'You\'re using the Dec 2026 deadline as the forcing function to advance the platform',
  'You want AI-grade extraction and modern UX without ongoing template authoring',
];

const FAQS = [
  {
    q: 'What is the Oracle WebCenter for OCI Marketplace?',
    a: 'Oracle WebCenter for OCI is a set of Oracle-published Linux images on the Oracle Cloud Marketplace (release 24.11.1 onwards) that contain Oracle WebCenter Products binaries (WebCenter Content 12.2.1.4), Fusion Middleware Infrastructure, and the JDK. Customers can deploy WebCenter on OCI as a customer-managed solution without re-architecting the application — the "lift-and-shift to OCI" path.',
  },
  {
    q: 'Is OCI Marketplace lift-and-shift cheaper than modernization?',
    a: 'It depends on the time horizon. The lift-and-shift implementation is typically less expensive than full modernization in absolute dollars, but the ongoing cost includes continued WebCenter license fees, OCI infrastructure costs, WebCenter expertise retention, and template/customization maintenance. Modernization is a higher one-time investment but eliminates the recurring WebCenter maintenance cost. Over a 5-year horizon, modernization is typically equal or lower TCO.',
  },
  {
    q: 'Does OCI Marketplace give us all the WebCenter modernization benefits?',
    a: 'No. OCI Marketplace addresses the infrastructure modernization — your WebCenter runs on Oracle Cloud instead of on-premises. The application layer (Forms Recognition templates, SOA composites, ADF forms, FIPSA accelerator) is unchanged. AI features in WebCenter 14c help, but they\'re augmentations of the existing architecture, not replacements.',
  },
  {
    q: 'Can we do Path A first and Path B later?',
    a: 'Yes, and this is a common sequence. Lift-and-shift to OCI Marketplace first (faster, lower-risk), then modernize off WebCenter when budget and strategic timing align. The downside is paying twice — once for the OCI lift-and-shift, again for the eventual modernization. The upside is breaking the work into manageable phases.',
  },
  {
    q: 'How do we decide which path is right?',
    a: 'The Modernization Assessment is designed to answer exactly this question. Andrew reads your existing WebCenter implementation directly — Forms Recognition templates, SOA composites, ADF customizations, supplier patterns, AP team workflow — and produces a forward-modernization plan that covers both Path A and Path B in your specific context. The deliverable is yours to keep whether you proceed to implementation with EZ Cloud or choose a Path A partner.',
  },
  {
    q: 'Will EZ Cloud help us with Path A if that\'s what we choose?',
    a: 'Yes. The 25-year founder-level WebCenter expertise that powers EZ Cloud\'s modernization assessments is equally available for Path A engagements — architecture review, FIPSA assessment, Forms Recognition rules migration audit, 12c → 14c upgrade planning, and OCI architecture and security setup. Whichever path you choose, the WebCenter AP expertise behind the decision is the same.',
  },
];

const WebCenterOciMarketplaceVsModernize = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle WebCenter on OCI Marketplace vs. Modernize — The December 2026 Decision',
    description:
      'A structured comparison of Path A (lift-and-shift Oracle WebCenter to OCI Marketplace) and Path B (modernize off WebCenter to a modern AP automation platform) for Oracle WebCenter AP customers facing the Fusion Middleware 12c December 2026 deadline.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-oci-marketplace-vs-modernize',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter on OCI Marketplace' },
      { '@type': 'Thing', name: 'Oracle WebCenter AP modernization' },
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 12c' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter on OCI Marketplace vs. Modernize — Path A vs. Path B | EZ Cloud"
        description="A structured comparison of lift-and-shift to Oracle WebCenter for OCI Marketplace (Path A) vs. modernize off WebCenter to a modern AP automation platform (Path B). Decision framework for the December 2026 Fusion Middleware 12c support deadline."
        canonical="https://ezcloud.ai/solutions/webcenter-oci-marketplace-vs-modernize"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'OCI Marketplace vs. Modernize',
            url: 'https://ezcloud.ai/solutions/webcenter-oci-marketplace-vs-modernize',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Layers className="w-4 h-4" aria-hidden />
              Path A vs. Path B
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              WebCenter on OCI Marketplace vs. modernize off WebCenter.
              <span className="block text-accent mt-3 text-3xl md:text-4xl lg:text-5xl">
                Which path is right for your AP operation?
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Both are valid forward paths from the December 2026 Fusion Middleware 12c support
              cliff. The right answer depends on your stack, your AP requirements, and your timing.
              Here's the framework.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/webcenter-12c-end-of-support-decision">
                  See the 12c support timeline
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The framing
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Two paths, one forcing function
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle Fusion Middleware 12c Premier Support ends December 2026. Every WebCenter
                  AP customer in the world has to respond.
                </strong>{' '}
                The two primary forward paths are Path A — lift-and-shift the existing WebCenter
                implementation onto Oracle Cloud Infrastructure using the Oracle WebCenter for OCI
                Marketplace images — or Path B — modernize off WebCenter to a modern AP automation
                platform that deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP.
              </p>
              <p>
                Both are legitimate strategic choices. Path A preserves the WebCenter architecture
                and shifts the infrastructure to OCI; Path B preserves Oracle ERP as system of
                record and shifts the AP automation layer to a modern platform. This page is the
                structured comparison.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The decision, drawn
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Where Path A and Path B sit among the forward paths
            </h2>
            <ForwardPathsDiagram />
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The decision matrix
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Path A vs. Path B — side by side
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              How each path scores across the dimensions that matter to a real WebCenter AP
              implementation decision.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/5">
                      Dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Path A — Lift-and-shift to OCI Marketplace
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Path B — Modernize off WebCenter (EZ Cloud)
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.dimension} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.dimension}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.pathA}</td>
                      <td className="px-5 py-4 text-gray-700">
                        <strong className="font-semibold text-gray-900">{row.pathB}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              When each path is the right answer
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-6 h-6 text-primary" aria-hidden />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Choose Path A when</h3>
                </div>
                <ul className="space-y-3">
                  {PATH_A_WHEN.map((reason) => (
                    <li key={reason} className="flex items-start gap-3">
                      <CheckCircle2
                        className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                        aria-hidden
                      />
                      <span className="text-gray-700 text-[15px] leading-relaxed">{reason}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-7 bg-white border border-accent/30 shadow-md">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Choose Path B when</h3>
                </div>
                <ul className="space-y-3">
                  {PATH_B_WHEN.map((reason) => (
                    <li key={reason} className="flex items-start gap-3">
                      <CheckCircle2
                        className="w-5 h-5 text-accent flex-shrink-0 mt-0.5"
                        aria-hidden
                      />
                      <span className="text-gray-700 text-[15px] leading-relaxed">{reason}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 md:p-10 bg-accent/5 border border-accent/30 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                The neutral decision call
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                We help customers choose either path
              </h3>
              <p className="text-gray-700 leading-relaxed mb-5">
                Most WebCenter consultancies sell Path A — keep WebCenter alive on OCI. EZ Cloud
                sells Path B — modernize off WebCenter. But the WebCenter AP Modernization
                Assessment is structured to evaluate both paths neutrally in your specific context.
                If Path A is genuinely right for you, we'll tell you that and we can do the Path A
                consulting work too.
              </p>
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the engagement tiers and modernization path
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </Card>
          </div>
        </section>

        {/* Decision Guide CTA */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured framework for Path A vs. Path B — four forward paths, stack-specific
              considerations, eight diagnostic questions, and vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on Path A vs. Path B
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Let's talk through your specific WebCenter situation
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              WebCenter AP specialist. We'll walk through your stack and the two paths in the
              context of your operation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterOciMarketplaceVsModernize;
